package com.castly.admin.ui.analytics

import androidx.compose.runtime.Composable
import com.castly.admin.ui.theme.*
import com.castly.shared.dto.HeatmapCell
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val dayLabels = listOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")

@Composable
fun HeatmapChart(
    cells: List<HeatmapCell>,
    title: String
) {
    val colors = AppTheme.colors
    val cellMap = cells.associateBy { it.dayOfWeek * 100 + it.hour }
    val maxCount = cells.maxOfOrNull { it.count } ?: 1L

    Div({
        style {
            cardStyle(colors)
        }
    }) {
        H3({
            style {
                margin(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.lg)
                color(Color(colors.text))
                fontWeight("600")
            }
        }) { Text(title) }

        if (cells.isEmpty()) {
            Div({
                style {
                    padding(Spacing.xxl)
                    textAlign("center")
                    color(Color(colors.textMuted))
                    fontSize(Typography.sm)
                }
            }) { Text("No listening data available") }
        } else {
            Div({
                style {
                    property("overflow-x", "auto")
                }
            }) {
                // Hour labels row
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "48px repeat(24, 1fr)")
                        gap(2.px)
                        marginBottom(2.px)
                    }
                }) {
                    Div({}) {} // empty corner
                    (0..23).forEach { h ->
                        Div({
                            style {
                                textAlign("center")
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                                padding(2.px)
                            }
                        }) { Text("${h}") }
                    }
                }

                // Day rows
                (0..6).forEach { dow ->
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "48px repeat(24, 1fr)")
                            gap(2.px)
                            marginBottom(2.px)
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                fontWeight("500")
                            }
                        }) { Text(dayLabels[dow]) }

                        (0..23).forEach { hour ->
                            val cell = cellMap[dow * 100 + hour]
                            val count = cell?.count ?: 0
                            val intensity = if (maxCount > 0) (count.toDouble() / maxCount).coerceIn(0.0, 1.0) else 0.0
                            val bg = cellColor(colors, intensity)

                            Div({
                                style {
                                    backgroundColor(Color(bg))
                                    borderRadius(2.px)
                                    height(24.px)
                                    minWidth(16.px)
                                    property("transition", "background-color 0.2s")
                                }
                                title("${dayLabels[dow]} ${hour}:00 — $count streams")
                            })
                        }
                    }
                }
            }
        }
    }
}

private fun cellColor(colors: AppColors, intensity: Double): String {
    if (intensity == 0.0) return colors.surfaceHover
    val alpha = (0.15 + intensity * 0.85).coerceIn(0.15, 1.0)
    val r = colors.primary.drop(1).take(2).toInt(16)
    val g = colors.primary.drop(3).take(2).toInt(16)
    val b = colors.primary.drop(5).take(2).toInt(16)
    return "rgba($r, $g, $b, $alpha)"
}
